/** Spending-by-category donut. Thickness and legend cap come from tokens. */
import { chart } from '@/lib/tokens'
import { t } from '@/lib/copy'

export interface DonutSlice {
  key: string
  label: string
  value: number
  color: string
}

export function DonutChart({
  slices,
  size = 220,
  centerLabel,
  centerValue,
  formatValue,
}: {
  slices: DonutSlice[]
  size?: number
  centerLabel: string
  centerValue: string
  formatValue: (value: number) => string
}) {
  const total = slices.reduce((sum, slice) => sum + Math.abs(slice.value), 0)
  const thickness = chart.donutThickness
  const radius = (size - thickness) / 2
  const circumference = 2 * Math.PI * radius

  let offset = 0
  const arcs = slices.map((slice) => {
    const share = total > 0 ? Math.abs(slice.value) / total : 0
    const length = share * circumference
    const arc = { slice, length, offset }
    offset += length
    return arc
  })

  return (
    <div className="score-dial" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={slices
          .map((slice) => t('a11y.chartBar', { label: slice.label, amount: formatValue(slice.value) }))
          .join('. ')}
      >
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--color-scoreTrack)" strokeWidth={thickness} />
        {arcs.map(({ slice, length, offset: start }) => (
          <circle
            key={slice.key}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={slice.color}
            strokeWidth={thickness}
            strokeDasharray={`${length} ${circumference - length}`}
            strokeDashoffset={-start}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          >
            <title>{t('a11y.chartBar', { label: slice.label, amount: formatValue(slice.value) })}</title>
          </circle>
        ))}
      </svg>
      <span className="score-dial-value" aria-hidden="true">
        <span className="metric-label">{centerLabel}</span>
        <span className="metric-value">{centerValue}</span>
      </span>
    </div>
  )
}

export function Legend({ slices }: { slices: DonutSlice[] }) {
  return (
    <ul className="chart-legend" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {slices.slice(0, chart.maxLegendItems).map((slice) => (
        <li key={slice.key} className="chart-legend-item">
          <span className="chart-legend-dot" style={{ background: slice.color }} aria-hidden="true" />
          {slice.label}
        </li>
      ))}
    </ul>
  )
}
